import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { UserWidgetConfigViewStore } from "../../store";
import { UserWidgetConfigView } from "../view/UserWidgetConfigView";
import { UserWidgetConfigUpdateStore } from "../../store";
import { UserWidgetConfigUpdate } from "../update/UserWidgetConfigUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type UserWidgetConfigModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const UserWidgetConfigModalContent: React.FC<UserWidgetConfigModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new UserWidgetConfigUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new UserWidgetConfigViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <UserWidgetConfigView
            userWidgetConfigViewStore={
              storeInfo.store as UserWidgetConfigViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <UserWidgetConfigUpdate
            userWidgetConfigUpdateStore={
              storeInfo.store as UserWidgetConfigUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
